import { Injectable } from '@angular/core';

/**
 * スマホで PC と同じ見た目を縮めて出す画面（稼働管理・不足時間確認・欠員募集）の共通処理
 *
 * 画面を開いている間だけ viewport を PC 相当の幅にして、PC と同じ見た目を縮めて出す。
 * 拡大は右端の「＋」「－」（MobileZoomComponent）で viewport の幅を段階で切り替えて行う。
 * 稼働管理だけはピンチを styles.less（body.mobile-zoom-nopinch）で止めている（iOS はピンチで拡大中にメニューやダイアログの位置がずれるため）。
 * ダイアログも同じ viewport で出るので、PC と同じ並びになる。
 *
 * 使い方：画面の ngOnInit で enter()、ngOnDestroy で leave() を呼ぶ。
 */
@Injectable({ providedIn: 'root' })
export class MobileZoomService {

  // 拡大の段階（viewport の幅）。先頭が一番小さい表示（全体）、後ろへ行くほど大きい
  static readonly ZOOM_WIDTHS = [1200, 850, 600, 420];

  // 通常の viewport（index.html と同じ）
  static readonly DEFAULT_VIEWPORT = 'width=device-width, initial-scale=1';

  // 画面を開いている間 body に付けるクラス（styles.less）
  static readonly BODY_CLASS = 'mobile-zoom';

  // ピンチで拡大させない画面で body に付けるクラス（styles.less。稼働管理だけ。
  // iOS はピンチで拡大中に、マスを押して出るメニューやダイアログの位置がずれるため。拡大は「＋」「－」で行う）
  static readonly NO_PINCH_CLASS = 'mobile-zoom-nopinch';

  // 拡大段階を残す localStorage のキー（3画面で共通）
  static readonly STORAGE_KEY = 'mobileZoom';

  // スマホで開いているか（ほかの画面と同じ 815px 以下。viewport を切り替える前に1回だけ判定する）
  readonly isMobile: boolean = window.matchMedia('(max-width: 815px)').matches;

  // 今の拡大段階（ZOOM_WIDTHS の添字）
  zoomIndex = 0;

  // 描画のやり直しをする画面か（enter の repaint。清掃員一覧だけ）
  private repaint = false;

  // 画面の中身が変わったのを見張る（描画のやり直し用）
  private observer: MutationObserver = null;

  // 描画のやり直しをまとめるタイマー
  private nudgeTimer: any = null;

  /**
   * 画面を開いたとき：viewport を切り替え、body に印を付ける（拡大段階は前回のものに戻す）。PC では何もしない
   * @param options noPinch：ピンチで拡大させない（稼働管理）。repaint：中身が変わるたびに描画をやり直させる（清掃員一覧）。省略時はどちらもしない
   */
  enter(options: { noPinch?: boolean, repaint?: boolean } = {}) {
    if (!this.isMobile) return;
    this.repaint = !!options.repaint;
    const saved = Number(localStorage.getItem(MobileZoomService.STORAGE_KEY));
    this.zoomIndex = (saved >= 0 && saved < MobileZoomService.ZOOM_WIDTHS.length) ? saved : 0;
    this.applyZoom();
    document.body.classList.add(MobileZoomService.BODY_CLASS);
    if (options.noPinch) {
      document.body.classList.add(MobileZoomService.NO_PINCH_CLASS);
    }
    if (this.repaint) {
      this.startObserver();
    }
  }

  /**
   * ほかの画面へ移るとき：viewport を元に戻し、body の印を外す。PC では何もしない
   */
  leave() {
    if (!this.isMobile) return;
    this.stopObserver();
    this.repaint = false;
    this.setViewport(MobileZoomService.DEFAULT_VIEWPORT);
    document.body.classList.remove(MobileZoomService.BODY_CLASS);
    document.body.classList.remove(MobileZoomService.NO_PINCH_CLASS);
  }

  /**
   * 画面の中身が変わったら（データの読み込み・ページ送り・ダイアログなど）描画をやり直させる（enter の repaint の画面だけ）
   * iPhone の Safari は viewport を切り替えたあと、「見えている範囲」を切り替え前の大きさのまま持っていて、
   * その外の文字をスクロールや拡大縮小があるまで描かないことがある。中身が変わるたびに少し揺らして描かせる
   */
  private startObserver() {
    this.stopObserver();
    if (typeof MutationObserver === 'undefined') return;
    this.observer = new MutationObserver(() => this.scheduleNudge());
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  private stopObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.nudgeTimer) {
      clearTimeout(this.nudgeTimer);
      this.nudgeTimer = null;
    }
  }

  /**
   * 描画のやり直しを少し待ってまとめて行う（Angular の描画は細かく何度も変わるため）
   */
  private scheduleNudge() {
    if (this.nudgeTimer) {
      clearTimeout(this.nudgeTimer);
    }
    this.nudgeTimer = setTimeout(() => {
      this.nudgeTimer = null;
      this.nudgeRepaint();
    }, 200);
  }

  /**
   * 描画をやり直させる：スクロールする入れ物（一覧・ダイアログ）を 1px 動かして戻す。Safari はこれで見えている範囲を計算し直す
   * 位置は元に戻すので、ユーザーのスクロール位置は変わらない
   */
  private nudgeRepaint() {
    const els = document.querySelectorAll('[class*="container"], .tbl-wrap, .mat-dialog-content');
    for (let i = 0; i < els.length; i++) {
      const el = els[i] as HTMLElement;
      if (!el || el.scrollHeight <= el.clientHeight) continue;
      const top = el.scrollTop;
      el.scrollTop = top + (top > 0 ? -1 : 1);
      el.scrollTop = top;
    }
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * 「＋」「－」：拡大段階を1つ変えて viewport を切り替える
   * @param n 1：拡大、-1：縮小
   */
  zoom(n: number) {
    const next = this.zoomIndex + n;
    if (next < 0 || next >= MobileZoomService.ZOOM_WIDTHS.length) return;
    this.zoomIndex = next;
    localStorage.setItem(MobileZoomService.STORAGE_KEY, String(next));
    this.applyZoom();
  }

  /**
   * 「＋」を押せるか（一番大きい表示では押せない）
   */
  canZoomIn(): boolean {
    return this.zoomIndex < MobileZoomService.ZOOM_WIDTHS.length - 1;
  }

  /**
   * 「－」を押せるか（一番小さい表示では押せない）
   */
  canZoomOut(): boolean {
    return this.zoomIndex > 0;
  }

  /**
   * 今の拡大段階の幅を viewport に反映する
   */
  private applyZoom() {
    this.setViewport('width=' + MobileZoomService.ZOOM_WIDTHS[this.zoomIndex]);
    // viewport が切り替わったあとに描画をやり直させる（切り替えが反映されるまで少し掛かるので2回）
    if (this.repaint) {
      setTimeout(() => this.nudgeRepaint(), 300);
      setTimeout(() => this.nudgeRepaint(), 1000);
    }
  }

  /**
   * viewport の meta を付け替える
   * 既存の meta の content を書き換えるだけだと拾わないブラウザがあるので、古い meta を外して新しい meta を追加する
   * PC の Chrome の開発者ツール（デバイスモード）は表示中の viewport の変更を反映しないことがあるので、確認は実機で行う
   * @param content meta の content
   */
  private setViewport(content: string) {
    const olds = document.querySelectorAll('meta[name="viewport"]');
    for (let i = 0; i < olds.length; i++) {
      olds[i].parentNode.removeChild(olds[i]);
    }
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'viewport');
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
}
